import clsx from 'clsx';
import _round from 'lodash/round';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

interface ProgressProps {
  isFetching: boolean;
}
export function Progress({ isFetching }: ProgressProps) {
  const { formatCurrency } = useCurrencySymbol();
  const {
    hiringTarget: { projectedBudget, hiringGoalSpend, targetBudget },
  } = useEditCampaignContext();

  const getPercentage = useCallback(
    (value: number) => {
      if (!value) return 0;
      return _round((value / targetBudget) * 100, 2);
    },
    [targetBudget],
  );

  const segments = useMemo(() => {
    return [
      {
        id: 'budget-spent',
        value: getPercentage(hiringGoalSpend),
        color: 'bg-fx-brand-600',
      },
      {
        id: 'remaining-budget',
        value: getPercentage(projectedBudget),
        color: 'bg-fx-brand-100',
      },
    ];
  }, [getPercentage, hiringGoalSpend, projectedBudget]);

  if (isFetching) {
    return (
      <div className="mt-4 h-12">
        <Skeleton className="h-2 w-full" />
      </div>
    );
  }

  return (
    <div className="relative mt-4 flex h-12 w-full flex-col gap-2">
      {/* Progress bar */}
      <div className="bg-muted flex h-2 w-full">
        {segments.map(seg => (
          <div
            key={seg.id}
            className={clsx('h-full', seg.color)}
            style={{ width: `${seg.value}%` }}
          />
        ))}
      </div>

      <div className="flex justify-between gap-4">
        <Typography variant="caption">
          <FormattedMessage
            defaultMessage="Spent: {hiringGoalSpend}"
            values={{ hiringGoalSpend: formatCurrency(hiringGoalSpend ?? 0) }}
            description="Bar legend: money already used toward the hire goal"
          />
        </Typography>
        <Typography variant="caption">
          <FormattedMessage
            defaultMessage="Remaining: {remainingBudget}"
            values={{ remainingBudget: formatCurrency(projectedBudget ?? 0) }}
            description="Bar legend: budget left in the current period"
          />
        </Typography>
      </div>

      <div className="pointer-events-none absolute inset-0 -top-2 flex">
        {segments.map((seg, idx) => {
          return (
            <div
              key={`marker-${seg.id}`}
              className="flex flex-col items-start"
              style={{ width: `${seg.value}%` }}
            >
              {idx === 1 && <div className="bg-fx-primary h-6 w-px" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
